// Intercepteur global des appels fetch() de l'application.
//
// Chaque ecran fait ses propres appels fetch('/api/...')
// avec le jeton Google stocke dans sessionStorage. Quand ce jeton expire ou
// devient invalide, le backend repond 403 (voir GoogleTokenFilter /
// SecurityConfig cote serveur), et jusqu'ici chaque ecran plantait de
// facon differente en essayant d'interpreter une reponse qu'il ne
// s'attendait pas a recevoir (ex: "Unexpected end of JSON input").
//
// Plutot que de repeter la verification dans chaque composant, on
// intercepte une seule fois, globalement, tous les appels fetch() : toute
// reponse 403 declenche une reconnexion via le SSO (ecran de connexion
// Google, voir Login.tsx) au lieu de laisser l'ecran appelant tenter
// d'exploiter une reponse invalide.
//
// A importer une seule fois, au plus tot au demarrage de l'appli (voir
// index.tsx) - l'import suffit, il n'y a rien a appeler explicitement.
import { signalerSessionExpiree } from './sessionExpired.ts';

const fetchNatif = window.fetch.bind(window);

// Jeton Google porte par l'appel, lu sans consommer la requete (ne pas
// reconstruire un Request a partir d'un Request : cela transfere son corps).
function jetonEnvoye(input: RequestInfo | URL, init?: RequestInit): string | null {
  const headers = input instanceof Request ? input.headers : new Headers(init?.headers);
  const valeur = headers.get('Authorization');
  if (!valeur || !valeur.startsWith('Bearer ')) return null;
  const jeton = valeur.substring(7).trim();
  return jeton === '' || jeton === 'null' || jeton === 'undefined' ? null : jeton;
}

window.fetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
  const [input, init] = args;
  const reponse = await fetchNatif(...args);

  // Expiration glissante : chaque appel authentifie renvoie un jeton de
  // session neuf (SessionTokenService cote serveur), valable 1 h
  // d'inactivite. Il remplace le jeton courant, y compris le jeton Google
  // initial (dont la duree de vie est fixe : 1 h apres la connexion).
  const jetonRenouvele = reponse.headers.get('X-Session-Token');
  if (jetonRenouvele && sessionStorage.getItem('token')) {
    sessionStorage.setItem('token', jetonRenouvele);
  }

  // 401 = jeton absent/invalide/expire (cf SecurityConfig) ; 403 conserve
  // pour compatibilite avec un backend pas encore redemarre.
  if (reponse.status === 401 || reponse.status === 403) {
    // Session expiree si le jeton envoye a ete refuse (duree de vie d'un
    // jeton Google : 1 h), ou s'il n'y a plus de jeton en session : un
    // appel parallele l'a deja detecte et efface, et les appels suivants
    // sont partis sans jeton.
    if (jetonEnvoye(input, init) || !sessionStorage.getItem('token')) {
      console.warn('Session expirée (' + reponse.status + ' du backend) : reconnexion SSO requise.');
      signalerSessionExpiree();
      // On ne leve PAS d'erreur : l'ecran appelant la laisserait
      // generalement non geree, et React l'afficherait en plein ecran
      // (overlay d'erreur) par-dessus l'ecran de connexion. La promesse
      // reste en attente : Login.tsx demonte l'application et affiche la
      // connexion Google, l'appelant n'a donc plus rien a traiter.
      return new Promise<Response>(() => {});
    }
    // Jeton present en session mais absent de l'appel : oubli dans le
    // code de l'ecran appelant, pas une session expiree.
    console.error('Appel API sans jeton d\'authentification (' + reponse.status + ') : ' + reponse.url);
  }

  return reponse;
};
